import { useEffect, useState } from 'react';
import { getUsbEnrollmentEvidence, type UsbEvidence } from '../../services/usbInstaller';
import { UsbEvidenceBadge } from './UsbInstallerViews';
import { OnenceSimCheck } from './OnenceSim';
import { Link } from 'react-router-dom';
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre';
import maplibregl, { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { formatGpsFixTime, formatLocationIndicator } from './gpsIndicator';
import {
  RadioWithOrg,
  Organization,
  InricoRadio,
  getAllRadios,
  getAllOrganizations,
  assignRadioOrg,
  deleteRadioById,
  updateRadioDetails,
  getPendingInricoRadios,
  getRejectedInricoRadios,
  authorizeInricoRadio,
  rejectInricoRadio,
  pushUpdateToRadio,
  getDeviceInfo,
} from '../../services/admin';

type PendingRadio = InricoRadio;

const lastKnownLocationMapStyle: StyleSpecification = {
  version: 8,
  sources: {
    openstreetmap: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'openstreetmap', type: 'raster', source: 'openstreetmap' }],
};

function formatLocationTimestamp(value: string): string {
  return new Intl.DateTimeFormat('da-DK', {
    dateStyle: 'short',
    timeStyle: 'medium',
    hour12: false,
  }).format(new Date(value));
}

export default function AssignManager() {
  const [radios, setRadios] = useState<RadioWithOrg[]>([]);
  const [networks, setNetworks] = useState<Organization[]>([]);
  const [pending, setPending] = useState<PendingRadio[]>([]);
  const [rejected, setRejected] = useState<PendingRadio[]>([]);
  // USB Installer evidence per pending radio (inrico_radios.id) - shown, never auto-approving.
  const [usbEvidence, setUsbEvidence] = useState<Record<string, UsbEvidence[]>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [pushingId, setPushingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<'radio_id' | 'name' | 'model' | 'status' | 'android_id' | 'org_name'>('radio_id');
  const [sortAsc, setSortAsc] = useState(true);
  const [locationRadio, setLocationRadio] = useState<RadioWithOrg | null>(null);
  const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy: number | null; source: string; fix_time: string | null } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  // Approval form state
  const [approvalForm, setApprovalForm] = useState<{
    radio: PendingRadio | null;
    name: string;
    radioId: string;
    formError: string | null;
  }>({ radio: null, name: '', radioId: '', formError: null });

  // Edit radio form state
  const [editForm, setEditForm] = useState<{
    radio: RadioWithOrg | null;
    name: string;
    model: string;
    radioId: string;
    orgId: string;
    formError: string | null;
  }>({ radio: null, name: '', model: '', radioId: '', orgId: '', formError: null });

  const openLocationModal = async (radio: RadioWithOrg) => {
    setLocationRadio(radio);
    setLocation(null);
    setLocationError(null);
    setLocationLoading(true);
    try {
      const info = await getDeviceInfo(radio.id);
      if (info.latitude == null || info.longitude == null) {
        setLocation(null);
      } else {
        setLocation({
          latitude: info.latitude,
          longitude: info.longitude,
          accuracy: info.accuracy_m,
          source: info.location_provider || 'Unknown',
          fix_time: info.location_timestamp ?? null,
        });
      }
    } catch (err) {
      setLocationError(err instanceof Error ? err.message : 'Failed to load last known location');
    } finally {
      setLocationLoading(false);
    }
  };

  const closeLocationModal = () => {
    setLocationRadio(null);
    setLocation(null);
    setLocationError(null);
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Same badge styling as the Devices page (RadiosManager.tsx) for visual consistency.
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-500/20 text-green-400 border-green-500';
      case 'offline':
        return 'bg-gray-500/20 text-gray-400 border-gray-500';
      case 'transmitting':
        return 'bg-red-500/20 text-red-400 border-red-500';
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
    }
  };

  const filteredRadios = radios.filter((r) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (r.radio_id || '').toLowerCase().includes(q) ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.model || '').toLowerCase().includes(q) ||
      (r.status || '').toLowerCase().includes(q) ||
      (r.android_id || '').toLowerCase().includes(q) ||
      (r.org_name || '').toLowerCase().includes(q)
    );
  });

  const sortedRadios = [...filteredRadios].sort((a, b) => {
    const va = (a[sortField] ?? '').toString().toLowerCase();
    const vb = (b[sortField] ?? '').toString().toLowerCase();
    if (va < vb) return sortAsc ? -1 : 1;
    if (va > vb) return sortAsc ? 1 : -1;
    return 0;
  });

  const sortIndicator = (field: typeof sortField) => (sortField === field ? (sortAsc ? ' ▲' : ' ▼') : '');

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      // Each fetch is isolated (not Promise.all) so one failing endpoint doesn't blank out the
      // others' already-successful data.
      const [r, n, p, rj] = await Promise.allSettled([
        getAllRadios(),
        getAllOrganizations(),
        getPendingInricoRadios(),
        getRejectedInricoRadios(),
      ]);
      if (r.status === 'fulfilled') setRadios(r.value);
      if (n.status === 'fulfilled') setNetworks(n.value);
      if (p.status === 'fulfilled') setPending(p.value);
      if (rj.status === 'fulfilled') setRejected(rj.value);
      getUsbEnrollmentEvidence().then(setUsbEvidence).catch(() => setUsbEvidence({}));

      const failures = [r, n, p, rj].filter((x): x is PromiseRejectedResult => x.status === 'rejected');
      if (failures.length > 0) {
        setError(failures.map((f) => (f.reason instanceof Error ? f.reason.message : 'Failed to load')).join('; '));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh so newly-registered pending devices show up without a manual reload.
    const interval = setInterval(() => load(true), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleApprove = async (p: PendingRadio) => {
    // Open approval form instead of directly approving
    setApprovalForm({ radio: p, name: '', radioId: '', formError: null });
  };

  const handleApprovalSubmit = async () => {
    if (!approvalForm.radio) return;

    const radioId = approvalForm.radioId.trim();
    if (!radioId) {
      setApprovalForm((prev) => ({ ...prev, formError: 'Radio ID is required' }));
      return;
    }
    if (radioId.length > 10) {
      setApprovalForm((prev) => ({ ...prev, formError: 'Radio ID must be 10 characters or fewer' }));
      return;
    }
    if (radios.some((r) => (r.radio_id || '').toLowerCase() === radioId.toLowerCase())) {
      setApprovalForm((prev) => ({ ...prev, formError: `Radio ID "${radioId}" is already used by another device` }));
      return;
    }

    // Radio name defaults to the Radio ID (no separate name field in this modal).
    const name = radioId;

    setApprovingId(approvalForm.radio.android_id);
    setError(null);
    try {
      await authorizeInricoRadio('', approvalForm.radio.android_id, true, name, radioId);
      await load();
      setApprovalForm({ radio: null, name: '', radioId: '', formError: null });
    } catch (e) {
      setApprovalForm((prev) => ({ ...prev, formError: e instanceof Error ? e.message : 'Failed to approve radio' }));
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (p: PendingRadio) => {
    if (!confirm('Reject this device? It moves to the Rejected pool and can be approved later.')) return;
    setApprovingId(p.android_id);
    setError(null);
    try {
      await rejectInricoRadio(p.android_id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reject radio');
    } finally {
      setApprovingId(null);
    }
  };

  const closeApprovalForm = () => {
    setApprovalForm({ radio: null, name: '', radioId: '', formError: null });
  };

  const handleDelete = async (radio: RadioWithOrg) => {
    if (!confirm(`Delete device "${radio.name || radio.radio_id}"? This cannot be undone.`)) return;
    setSavingId(radio.id);
    setError(null);
    try {
      await deleteRadioById(radio.id);
      setRadios((prev) => prev.filter((r) => r.id !== radio.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setSavingId(null);
    }
  };

  const handlePushUpdate = async (radio: RadioWithOrg) => {
    setPushingId(radio.id);
    setError(null);
    setInfo(null);
    try {
      await pushUpdateToRadio(radio.id);
      setInfo(`Update check pushed to "${radio.name || radio.radio_id}".`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to push update');
    } finally {
      setPushingId(null);
    }
  };

  const openEdit = (radio: RadioWithOrg) => {
    setEditForm({
      radio,
      name: radio.name ?? '',
      model: radio.model ?? '',
      radioId: radio.radio_id ?? '',
      orgId: radio.org_id ?? '',
      formError: null,
    });
  };

  const closeEdit = () => {
    setEditForm({ radio: null, name: '', model: '', radioId: '', orgId: '', formError: null });
  };

  const handleEditSubmit = async () => {
    if (!editForm.radio) return;
    const radioId = editForm.radioId.trim();
    if (!radioId) {
      setEditForm((prev) => ({ ...prev, formError: 'Radio ID is required' }));
      return;
    }
    if (radios.some((r) => r.id !== editForm.radio!.id && (r.radio_id || '').toLowerCase() === radioId.toLowerCase())) {
      setEditForm((prev) => ({ ...prev, formError: `Radio ID "${radioId}" is already used by another device` }));
      return;
    }
    setSavingId(editForm.radio.id);
    setError(null);
    try {
      await updateRadioDetails(editForm.radio.id, {
        radio_id: radioId,
        model: editForm.model.trim(),
        name: editForm.name.trim(),
      });
      await assignRadioOrg(editForm.radio.id, editForm.orgId === '' ? null : editForm.orgId);
      await load(true);
      closeEdit();
    } catch (e) {
      setEditForm((prev) => ({ ...prev, formError: e instanceof Error ? e.message : 'Failed to update radio' }));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold">🔗 Assign Radios</h2>
        <p className="text-gray-400 mt-1">
          All devices/radios and the network each one is assigned to (or None).
        </p>
      </div>

      {error && (
        <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-200">
          {error}
        </div>
      )}

      {info && (
        <div className="bg-blue-900/40 border border-blue-700 rounded-lg p-3 text-blue-200">
          {info}
        </div>
      )}

      {/* Pending device approval */}
      <div className="space-y-3">
        <h3 className="text-xl font-bold">🔔 Pending Device Approval</h3>
        {loading ? (
          <div className="text-gray-400">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-4 text-gray-400 border border-gray-700">
            ✅ No pending radios. All devices have been authorized.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {pending.map((p) => (
              <div
                key={p.android_id}
                className="bg-gray-800 rounded-lg p-4 border border-yellow-600 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">Android ID</p>
                    <p className="font-mono text-white text-sm">{p.android_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold" title="Sent by the radio at enrollment (lower trust than a USB observation)">ICCID (reported by radio)</p>
                    <p className="font-mono text-white text-sm">{p.iccid}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">Model</p>
                    <p className="text-white">{p.model}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">Status</p>
                    <p className="text-yellow-400 font-semibold capitalize">{p.registration_status}</p>
                  </div>
                  <div className="col-span-2 md:col-span-4">
                    <UsbEvidenceBadge evidence={usbEvidence[p.id]} />
                  </div>
                  <div className="col-span-2 md:col-span-4">
                    <OnenceSimCheck kind="inrico_radio" id={p.id} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(p)}
                    disabled={approvingId === p.android_id}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold py-1.5 px-3 rounded"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(p)}
                    disabled={approvingId === p.android_id}
                    className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm font-semibold py-1.5 px-3 rounded"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rejected devices - kept separate from Pending so "Reject" isn't a no-op; still
          approvable later via the same Approve flow. */}
      {rejected.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xl font-bold">🚫 Rejected Devices</h3>
          <div className="grid grid-cols-1 gap-3">
            {rejected.map((p) => (
              <div
                key={p.android_id}
                className="bg-gray-800 rounded-lg p-4 border border-red-800/60 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">Android ID</p>
                    <p className="font-mono text-white text-sm">{p.android_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">ICCID</p>
                    <p className="font-mono text-white text-sm">{p.iccid}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">Model</p>
                    <p className="text-white">{p.model}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-semibold">Status</p>
                    <p className="text-red-400 font-semibold">Rejected</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(p)}
                    disabled={approvingId === p.android_id}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold py-1.5 px-3 rounded"
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Radio-to-network assignment */}
      <h3 className="text-xl font-bold pt-2">📡 Devices &amp; Network Assignment</h3>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search devices..."
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
      />
      {loading ? (
        <div className="text-gray-400">Loading…</div>
      ) : radios.length === 0 ? (
        <div className="text-gray-400">No radios found.</div>
      ) : sortedRadios.length === 0 ? (
        <div className="text-gray-400">No devices match your search.</div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-900 text-gray-400 text-sm">
              <tr>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('radio_id')}>
                  Radio ID{sortIndicator('radio_id')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('name')}>
                  Radio Name{sortIndicator('name')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('model')}>
                  Model{sortIndicator('model')}
                </th>
                <th className="px-4 py-3">Device Type</th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('status')}>
                  Status{sortIndicator('status')}
                </th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('android_id')}>
                  Android ID{sortIndicator('android_id')}
                </th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3 cursor-pointer select-none hover:text-white" onClick={() => handleSort('org_name')}>
                  Network{sortIndicator('org_name')}
                </th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRadios.map((radio) => {
                const hasUpdate =
                  radio.latest_version_code != null &&
                  (radio.app_version_code == null || radio.latest_version_code > radio.app_version_code);
                return (
                <tr key={radio.id} className="border-t border-gray-700">
                  <td className="px-4 py-3 font-mono text-sm text-gray-300">{radio.radio_id}</td>
                  <td className="px-4 py-3 font-medium">{radio.name || '—'}</td>
                  <td className="px-4 py-3 text-gray-300">{radio.model || '—'}</td>
                  <td className="px-4 py-3 text-gray-300 capitalize">{radio.device_type || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border capitalize ${getStatusColor(radio.status)}`}>
                      {radio.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-300">
                    {radio.android_id ? (
                      <Link to={`/Admin/device/${radio.id}`} className="text-blue-400 hover:text-blue-300 hover:underline">
                        {radio.android_id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-sm whitespace-nowrap">
                    {radio.app_version_name || '—'}
                    {hasUpdate && (
                      <button
                        onClick={() => handlePushUpdate(radio)}
                        disabled={radio.status !== 'online' || pushingId === radio.id}
                        title={radio.status !== 'online' ? 'Radio must be online to receive a push' : `Push update ${radio.latest_version_name} now`}
                        className="ml-2 inline-flex text-yellow-400 hover:text-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {pushingId === radio.id ? '…' : '⬆'}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{radio.org_name || 'None'}</td>
                  <td className="px-4 py-3 text-gray-400 text-sm whitespace-nowrap">
                    {radio.last_location_at ? (
                      <button
                        onClick={() => openLocationModal(radio)}
                        className="text-left text-blue-300 hover:text-blue-200 hover:underline"
                        title="Show last known location"
                      >
                        {formatLocationIndicator(radio.last_location_at, radio.gps_status)}
                      </button>
                    ) : formatLocationIndicator(null, radio.gps_status)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2 items-center">
                      <button
                        onClick={() => openEdit(radio)}
                        disabled={savingId === radio.id}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded"
                      >                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(radio)}
                        disabled={savingId === radio.id}
                        className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium py-1.5 px-3 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Approval Form Modal */}
      {approvalForm.radio && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md p-6 space-y-4">
            <h3 className="text-2xl font-bold text-white">Approve Radio</h3>
            
            <div className="space-y-2 bg-gray-900 rounded p-3 border border-gray-700">
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold">Android ID</p>
                <p className="font-mono text-white">{approvalForm.radio.android_id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold">Model</p>
                <p className="text-white">{approvalForm.radio.model}</p>
              </div>
            </div>

            {approvalForm.formError && (
              <div className="bg-red-900/40 border border-red-700 rounded p-3 text-red-200 text-sm">
                {approvalForm.formError}
              </div>
            )}

            <div className="space-y-2">
              <label className="block">
                <p className="text-sm font-semibold text-gray-300 mb-1">Radio ID (max 10 characters) *</p>
                <input
                  type="text"
                  placeholder="e.g., S0001"
                  maxLength={10}
                  value={approvalForm.radioId}
                  onChange={(e) => setApprovalForm((prev) => ({ ...prev, radioId: e.target.value.slice(0, 10), formError: null }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-green-600 focus:border-transparent font-mono"
                />
              </label>
              <p className="text-xs text-gray-400">Any text, up to 10 characters.</p>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <button
                onClick={closeApprovalForm}
                disabled={approvingId === approvalForm.radio.android_id}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleApprovalSubmit}
                disabled={approvingId === approvalForm.radio.android_id}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
              >
                {approvingId === approvalForm.radio.android_id ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Radio Modal */}
      {editForm.radio && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md p-6 space-y-4">
            <h3 className="text-2xl font-bold text-white">Edit Radio</h3>

            {editForm.formError && (
              <div className="bg-red-900/40 border border-red-700 rounded p-3 text-red-200 text-sm">
                {editForm.formError}
              </div>
            )}

            <label className="block">
              <p className="text-sm font-semibold text-gray-300 mb-1">Radio Name</p>
              <input
                type="text"
                placeholder="e.g., Dispatch 01"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value, formError: null }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </label>

            <label className="block">
              <p className="text-sm font-semibold text-gray-300 mb-1">Radio ID *</p>
              <input
                type="text"
                placeholder="e.g., S0001"
                value={editForm.radioId}
                onChange={(e) => setEditForm((prev) => ({ ...prev, radioId: e.target.value, formError: null }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono"
              />
            </label>

            <label className="block">
              <p className="text-sm font-semibold text-gray-300 mb-1">Model</p>
              <input
                type="text"
                placeholder="e.g., S380"
                value={editForm.model}
                onChange={(e) => setEditForm((prev) => ({ ...prev, model: e.target.value, formError: null }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </label>

            <label className="block">
              <p className="text-sm font-semibold text-gray-300 mb-1">Network</p>
              <select
                value={editForm.orgId}
                onChange={(e) => setEditForm((prev) => ({ ...prev, orgId: e.target.value, formError: null }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option value="">None</option>
                {networks.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3 bg-gray-900 rounded p-3 border border-gray-700">
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold">Android ID</p>
                <p className="font-mono text-white text-sm">{editForm.radio.android_id || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold">Status</p>
                <p className="text-white text-sm capitalize">{editForm.radio.status}</p>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <button
                onClick={closeEdit}
                disabled={savingId === editForm.radio.id}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={savingId === editForm.radio.id}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded"
              >
                {savingId === editForm.radio.id ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {locationRadio && (
        <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">Last known GPS location</h3>
                <p className="text-gray-400 text-sm">
                  {locationRadio.radio_id} · {locationRadio.name || 'Unnamed radio'} · {locationRadio.status}
                </p>
              </div>
              <button onClick={closeLocationModal} className="text-gray-400 hover:text-white" aria-label="Close">
                ✕
              </button>
            </div>

            {locationLoading && (
              <div className="h-96 flex items-center justify-center text-gray-400">Loading last known location...</div>
            )}

            {!locationLoading && locationError && (
              <div className="h-32 flex items-center justify-center text-red-300">{locationError}</div>
            )}

            {!locationLoading && !locationError && !location && (
              <div className="h-32 flex items-center justify-center text-gray-400">
                No known GPS position has been reported by this radio.
              </div>
            )}

            {!locationLoading && !locationError && location && (
              <>
                <div className="h-[28rem] rounded-lg overflow-hidden border border-gray-700">
                  <Map
                    initialViewState={{ longitude: location.longitude, latitude: location.latitude, zoom: 15 }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle={lastKnownLocationMapStyle}
                    mapLib={maplibregl as any}
                  >
                    <NavigationControl position="top-left" />
                    <Marker longitude={location.longitude} latitude={location.latitude} anchor="bottom">
                      <div className="text-3xl drop-shadow">📍</div>
                    </Marker>
                  </Map>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">GPS fix time</p>
                    <p className="text-gray-200">{formatGpsFixTime(location.fix_time, formatLocationTimestamp)}</p>
                  </div>
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">Last online</p>
                    <p className="text-gray-200">{formatLocationTimestamp(locationRadio.updated_at)}</p>
                  </div>
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">Coordinates</p>
                    <p className="text-gray-200 font-mono">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
                  </div>
                  <div className="bg-gray-900 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase">Source / accuracy</p>
                    <p className="text-gray-200">{location.source}{location.accuracy != null ? ` · ±${Math.round(location.accuracy)} m` : ''}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
